import { Button, TextInput, Spacing, FormField, Checkbox } from 'zens-storybook'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import { object, string } from 'yup'
import { useStore, ProfileStatus, ApiError, useProfile } from 'zens-store'
import { useRouter, useNumberParams, useParams, Redirect } from 'zens-router'
import { AuthPanel, AuthPasswordHelp } from '@dms/component'
import { ValidationError } from '@dms/helper'

const styles = StyleSheet.create({
  input: {
    marginBottom: Spacing.XS,
    minHeight: 92,
  },
  email: {
    marginHorizontal: Spacing.XL,
    marginBottom: Spacing.L,
  },
  button: {
    minWidth: 100,
  },
})

const AuthSignUp = () => {
  const { t } = useTranslation()
  const [{ companyId }] = useNumberParams()
  const [{ token, name }] = useParams()
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const { auth, message } = useStore()
  const profile = useProfile({ token })

  const schema = object({
    email: string().required(t('Email address is required')),
    password: string()
      .required(t('A password is required'))
      .min(6, t('Password must be at least 6 characters'))
      .matches(/[a-z]+/, t('Password must contain at least one lowercase and one uppercase letter'))
      .matches(/[A-Z]+/, t('Password must contain at least one lowercase and one uppercase letter'))
      .matches(/[0-9]+/, t('Password must contain at least one number')),
  })

  useEffect(() => {
    if (profile.data?.id && auth.profile?.id === profile.data?.id) {
      console.debug('Already logged in correctly')
      if (companyId) router.push(`/companies/${companyId}`)
      else router.push(`/`)
    } else if (profile.data?.status === ProfileStatus.Active) {
      const email = profile.data?.email
      router.push({ pathname: `/signin`, params: { email, companyId } })
    }
  }, [profile.data])

  useEffect(() => {
    if (profile.isError) {
      const { message: errorMessage } = profile.error as Error
      console.debug(`AUTHPASSWORD: Profile load failed: ${errorMessage}`)
      message.add({
        message: `${t('Profile data could not be loaded')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }, [profile.isError])

  useEffect(() => {
    setErrors({})
  }, [password])

  const signUp = async () => {
    try {
      schema.validateSync({ email: profile.data?.email, name, password })
    } catch (error) {
      const { message, path } = error as ValidationError
      setErrors({ [path]: message })
      return
    }

    setLoading(true)
    try {
      if (!token || !name || !profile.data?.email) throw new Error('Invalid data')
      setErrors({})
      await auth.signUp({
        token,
        email: profile.data?.email,
        name,
        password,
      })
      if (remember) await auth.rememberAuth()
      router.push(`/companies/${companyId}`)
      setLoading(false)
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`AUTH: Sign up failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Sign up failed')}. ${errorMessage}`,
          status: 'error',
        })
      }
      setLoading(false)
    }
  }

  if (!profile.isFetched) return <View />
  if (profile.data?.email === undefined) {
    return <Redirect href={{ pathname: '/forbidden' }} />
  }

  return (
    <AuthPanel
      backHref={{ pathname: `/name`, params: { name, token, companyId } }}
      title={t('Welcome')}
      bottom={
        <Button
          title={t('Sign up')}
          style={styles.button}
          onPress={signUp}
          testID="AuthSignUp.Submit"
          disabled={loading}
        />
      }
    >
      <FormField disabled error={errors.email} style={styles.email}>
        <TextInput value={profile.data?.email} disabled error={!!errors.email} round />
      </FormField>
      <FormField
        label={t('Choose password')}
        error={errors.password}
        style={styles.input}
        help={<AuthPasswordHelp value={password} />}
      >
        <TextInput
          placeholder={t('Enter your password here')}
          value={password}
          onChangeText={setPassword}
          onSubmitEditing={signUp}
          autoCapitalize="none"
          error={!!errors.password}
          secureTextEntry
          autoFocus
          testID="AuthSignUp.Password"
        />
      </FormField>
      <Checkbox
        label={t('Keep me signed in')}
        value={remember}
        onValueChange={setRemember}
        testID="AuthSignUp.Remember"
      />
    </AuthPanel>
  )
}

export default AuthSignUp
